/**
 * StateDisplay Component
 * Provides enhanced loading and empty state displays with prominence and positioning
 * Implements smooth transitions between different display states
 */

import React from 'react'
import styles from './StateDisplay.module.css'
import '../styles/animations.css'

interface StateDisplayProps {
  state: 'loading' | 'empty' | 'error'
  prominence?: 'high' | 'medium' | 'low'
  position?: 'below-form' | 'results-area' | 'sidebar'
  animateEntry?: boolean
  className?: string
  // Loading state props
  loadingMessage?: string
  loadingProgress?: number
  // Empty state props
  emptyTitle?: string
  emptyMessage?: string
  emptyActionText?: string
  onEmptyAction?: () => void
  // Error state props
  errorTitle?: string
  errorMessage?: string
  errorActionText?: string
  onErrorAction?: () => void
}

const StateDisplay: React.FC<StateDisplayProps> = ({
  state,
  prominence = 'medium',
  position = 'below-form',
  animateEntry = true,
  className = '',
  // Loading props
  loadingMessage = 'Calculating ROI...',
  loadingProgress,
  // Empty props
  emptyTitle = 'Ready to Calculate',
  emptyMessage = 'Enter your project parameters and click "Calculate ROI" to see your results.',
  emptyActionText = 'Get Started',
  onEmptyAction,
  // Error props
  errorTitle = 'Something went wrong',
  errorMessage = 'Please try again or contact support if the problem persists.',
  errorActionText = 'Try Again',
  onErrorAction
}) => {
  const containerClasses = [
    styles.stateDisplay,
    styles[state],
    styles[`prominence${prominence.charAt(0).toUpperCase() + prominence.slice(1)}`],
    styles[`position${position.charAt(0).toUpperCase() + position.slice(1).replace('-', '')}`],
    animateEntry ? styles.animateEntry : '',
    animateEntry ? 'slide-in-up' : '',
    'transition-all',
    className
  ].filter(Boolean).join(' ')

  const renderLoadingState = () => (
    <div className={styles.loadingContent} role="status" aria-live="polite">
      <div className={styles.spinnerContainer}>
        <div className={styles.spinner} aria-hidden="true">
          <div className={styles.spinnerRing}></div>
          <div className={styles.spinnerRing}></div>
          <div className={styles.spinnerRing}></div>
        </div>
        {loadingProgress !== undefined && (
          <div className={styles.progressContainer}>
            <div 
              className={styles.progressBar}
              style={{ width: `${Math.min(100, Math.max(0, loadingProgress))}%` }}
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      
      <div className={styles.textContent}>
        <h3 className={styles.title}>{loadingMessage}</h3>
        {loadingProgress !== undefined && (
          <p className={styles.subtitle}>
            {Math.round(Math.min(100, Math.max(0, loadingProgress)))}% complete
          </p>
        )}
        <p className={styles.description}>
          This may take a few moments while we calculate your ROI analysis.
        </p>
      </div>

      {/* Screen reader announcement */}
      <div className={styles.srOnly} aria-live="polite" aria-atomic="true">
        {loadingMessage}
        {loadingProgress !== undefined && ` ${Math.round(Math.min(100, Math.max(0, loadingProgress)))}% complete`}
      </div>
    </div>
  )

  const renderEmptyState = () => (
    <div className={styles.emptyContent} role="status" aria-live="polite">
      <div className={styles.iconContainer}>
        <svg 
          className={styles.emptyIcon} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5} 
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
          />
        </svg>
      </div>
      
      <div className={styles.textContent}>
        <h3 className={styles.title}>{emptyTitle}</h3>
        <p className={styles.description}>{emptyMessage}</p>
        
        <div className={styles.guidanceList}>
          <div className={`${styles.guidanceItem} transition-opacity`}>
            <svg className={styles.guidanceIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
            </svg>
            <span>Fill in your project details above</span>
          </div>
          <div className={`${styles.guidanceItem} transition-opacity`}>
            <svg className={styles.guidanceIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
            </svg>
            <span>Select your industry and dosage requirements</span>
          </div>
          <div className={`${styles.guidanceItem} transition-opacity`}>
            <svg className={styles.guidanceIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
            </svg>
            <span>Click "Calculate ROI" to see your analysis</span>
          </div>
        </div>

        {onEmptyAction && (
          <button
            type="button"
            onClick={onEmptyAction}
            className={`${styles.actionButton} button-transition button-hover-lift button-active-press focus-ring-animate`}
            aria-describedby="empty-state-description"
          >
            {emptyActionText}
          </button>
        )}
      </div>

      <div id="empty-state-description" className={styles.srOnly}>
        {emptyMessage}
      </div>
    </div>
  )

  const renderErrorState = () => (
    <div className={styles.errorContent} role="alert" aria-live="assertive">
      <div className={styles.iconContainer}>
        <svg 
          className={styles.errorIcon} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
      </div>
      
      <div className={styles.textContent}>
        <h3 className={styles.title}>{errorTitle}</h3>
        <p className={styles.description}>{errorMessage}</p>
        
        <div className={styles.troubleshootingList}>
          <h4 className={styles.troubleshootingTitle}>What you can try:</h4>
          <ul className={styles.troubleshootingItems}>
            <li>Check your internet connection</li>
            <li>Verify all form fields are filled correctly</li>
            <li>Try refreshing the page</li>
            <li>Contact support if the issue persists</li>
          </ul>
        </div>

        {onErrorAction && (
          <button
            type="button"
            onClick={onErrorAction}
            className={`${styles.actionButton} button-transition button-hover-lift button-active-press focus-ring-animate`}
            aria-describedby="error-state-description"
          >
            {errorActionText}
          </button>
        )}
      </div>

      <div id="error-state-description" className={styles.srOnly}>
        {errorMessage}
      </div>
    </div>
  )

  const renderStateContent = () => {
    switch (state) {
      case 'loading':
        return renderLoadingState()
      case 'empty':
        return renderEmptyState()
      case 'error':
        return renderErrorState()
      default:
        return null
    }
  }

  return (
    <div className={containerClasses} data-testid={`state-display-${state}`}>
      {renderStateContent()}
    </div>
  )
}

export default StateDisplay